import { useState } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { X, Upload, ShieldCheck, Ban, AlertTriangle } from "lucide-react";

// Iter 123 — Player-initiated cancellation for an ACTIVE iter3 contract
// (streamer non-delivery). Reason + proof of wager delivered so far both
// required. On approve, admin runs settlement on their verified delivered
// wager, releases streamer reservation minus 100% unlock, marks contract
// `cancelled`. Player never sees VG credit mechanics.

export default function CancellationRequestModal({ contract, onClose, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [proofs, setProofs] = useState([]);          // [{id, path, filename}]
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    setUploading(true);
    try {
      const added = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: data.id, path: data.path, filename: data.filename, contentType: data.contentType });
      }
      setProofs((old) => [...old, ...added]);
      toast.success(`Uploaded ${files.length} proof${files.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUploading(false); }
  };

  const removeProof = async (idx) => {
    const p = proofs[idx];
    try { await api.delete(`/players/me/proofs?path=${encodeURIComponent(p.path)}`); } catch {}
    setProofs(proofs.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("Describe what the streamer failed to deliver."); return; }
    if (proofs.length === 0) { toast.error("Attach at least one proof of the wager delivered so far."); return; }
    setBusy(true);
    try {
      await api.post(`/players/me/contracts/${contract.id}/request-cancellation`, {
        reason: reason.trim(),
        proofFileIds: proofs.map((p) => p.id).filter(Boolean),
      });
      toast.success("Cancellation requested — admin will review.");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="cxl-modal">
      <div className="w-full max-w-lg bg-[#0B1A14] border border-[#F26D6D]/40 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 sticky top-0 bg-[#0B1A14]">
          <div className="flex items-center gap-2"><Ban className="w-4 h-4 text-[#F26D6D]" /><h3 className="font-display font-bold text-base">Request cancellation</h3></div>
          <button data-testid="cxl-close" onClick={onClose} className="text-[#8B97AE] hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 text-[11px] text-[#F5B841] border border-[#F5B841]/30 bg-[#F5B841]/[0.04] p-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>Cancellation isn't automatic — an admin reviews your reason + proof, verifies the delivered wager, and decides.</span>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-1">What did the streamer fail to deliver? (required)</label>
            <textarea data-testid="cxl-reason" rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-none bg-transparent border border-white/20 text-xs text-[#E8ECF4] p-2 font-mono outline-none focus:border-[#F26D6D] resize-none" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] block mb-2">Proof of wager delivered so far (required)</label>
            <label className="inline-flex items-center gap-2 px-3 h-10 border border-white/20 cursor-pointer hover:bg-white/5">
              <Upload className="w-3 h-3" />
              <span className="text-[11px] font-mono uppercase tracking-widest">{uploading ? "Uploading…" : "Add proof"}</span>
              <input data-testid="cxl-proof-input" type="file" multiple accept="image/*,application/pdf,video/*" className="hidden"
                onChange={(e) => { if (e.target.files?.length) upload([...e.target.files]); e.target.value = ""; }} />
            </label>
            {proofs.length > 0 && (
              <ul className="mt-3 space-y-1" data-testid="cxl-proof-list">
                {proofs.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 border border-white/10 px-2 py-1 text-[11px] font-mono">
                    <ShieldCheck className="w-3 h-3 text-[#2DD4A7]" />
                    <span className="text-[#E8ECF4] truncate flex-1">{p.filename}</span>
                    <button onClick={() => removeProof(i)} className="text-[#F26D6D] hover:text-white" data-testid={`cxl-proof-remove-${i}`}><X className="w-3 h-3" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="pt-2 border-t border-white/10 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 h-9 border border-white/20 text-xs font-mono uppercase tracking-widest text-[#8B97AE] hover:text-[#E8ECF4]">Never mind</button>
            <button data-testid="cxl-submit" onClick={submit} disabled={busy || !reason.trim() || proofs.length === 0}
              className="px-4 h-9 bg-[#F26D6D] text-white text-xs font-mono uppercase tracking-widest hover:opacity-90 disabled:opacity-40">{busy ? "…" : "Submit for admin review"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
